import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PlayIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { featuredVideos, FeaturedVideo } from '../data/featured-videos';
const EASE = [0.23, 1, 0.32, 1] as const;

/** One title request per video, shared by every repeated card. */
const titleCache = new Map<string, Promise<string>>();

/** Looks up a video's title from YouTube when none is set in the data. */
function useYouTubeTitle(video: FeaturedVideo) {
  const [title, setTitle] = useState(video.title ?? '');
  useEffect(() => {
    if (video.title) return;
    let cancelled = false;
    let request = titleCache.get(video.youtubeId);
    if (!request) {
      request = fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(`https://www.youtube.com/watch?v=${video.youtubeId}`)}`).then(r => r.ok ? r.json() : null).then((data: {
        title?: string;
      } | null) => data?.title ?? '').catch(() => '');
      titleCache.set(video.youtubeId, request);
    }
    request.then(t => {
      if (!cancelled && t) setTitle(t);
    });
    return () => {
      cancelled = true;
    };
  }, [video.title, video.youtubeId]);
  return title;
}
function VideoCard({
  video,
  isEs,
  onPlay
}: {
  video: FeaturedVideo;
  isEs: boolean;
  onPlay: (video: FeaturedVideo, title: string) => void;
}) {
  const fetched = useYouTubeTitle(video);
  const title = isEs && video.titleEs || fetched;
  return <button type="button" onClick={() => onPlay(video, title)} className="group flex-shrink-0 w-72 sm:w-[22rem] mx-2.5 text-left focus:outline-none" aria-label={`${isEs ? 'Reproducir' : 'Play'}${title ? `: ${title}` : ''}`} data-id="element-3579" data-mp="33wabvypv60rr">
      <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-haa-navy shadow-sm ring-1 ring-black/5 transition-shadow duration-200 group-hover:shadow-lg group-focus-visible:ring-2 group-focus-visible:ring-haa-blue" data-id="element-3580" data-mp="x2exrhfs5y13">
        {/* Only a lightweight thumbnail loads — the player loads on click */}
        <img src={`https://i.ytimg.com/vi/${video.youtubeId}/hqdefault.jpg`} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.03]" data-id="element-3581" data-mp="2rcv66c057rea" />
        <span className="absolute inset-0 bg-black/20 transition-colors duration-200 group-hover:bg-black/30" data-id="element-3582" data-mp="35c4n5dw6apjb" />
        <span className="absolute inset-0 flex items-center justify-center" data-id="element-3583" data-mp="3a9nz275eeq83">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-haa-navy shadow-lg transition-transform duration-200 ease-out group-hover:scale-105" data-id="element-3584" data-mp="2vdowfc125iac">
            <PlayIcon className="ml-0.5 h-6 w-6 fill-current" data-id="element-3585" data-mp="1jx8lnw0g7ptt" />
          </span>
        </span>
      </div>
      <p className="mt-3 font-nunito text-sm font-bold leading-snug text-haa-navy line-clamp-2 min-h-[2.5rem]" data-id="element-3586" data-mp="1ox3cjzk84fev">
        {title}
      </p>
    </button>;
}

/**
 * Horizontally scrolling row of YouTube videos. Cards show only a
 * thumbnail; the YouTube player loads only when a visitor opens a video,
 * in a large overlay on the same page.
 */
export function VideoScroller() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [playing, setPlaying] = useState<{
    video: FeaturedVideo;
    title: string;
  } | null>(null);
  useEffect(() => {
    if (!playing) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPlaying(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [playing]);
  if (featuredVideos.length === 0) return null;

  // Repeat the list so each half of the loop is wider than any screen,
  // then double it so the marquee can wrap seamlessly.
  const repeats = Math.max(1, Math.ceil(6 / featuredVideos.length));
  const half = Array.from({
    length: repeats
  }, () => featuredVideos).flat();
  const loop = [...half, ...half];
  return <section className="bg-white py-12" aria-label={isEs ? 'Videos' : 'Videos'} data-id="element-3587" data-mp="3j9uhjfco3wkg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3588" data-mp="3o4qmfg306eez">
        <div className="mb-6 flex items-end justify-between gap-4" data-id="element-3589" data-mp="3t0p82vwy5ibp">
          <div data-id="element-3590" data-mp="xoagbi38ncv">
            <span className="font-nunito text-haa-blue text-xs uppercase tracking-[0.3em] font-semibold block mb-1" data-id="element-3591" data-mp="x6fdi75wf6j">
              {isEs ? 'Videos' : 'Videos'}
            </span>
            <h2 className="font-bebas text-3xl sm:text-4xl tracking-[0.03em] text-haa-navy leading-tight" data-id="element-3592" data-mp="avvmqh1fmh8j">
              {isEs ? 'Mire y Comparta' : 'Watch & Share'}
            </h2>
          </div>
        </div>

      </div>

      {/* Continuously scrolling row; pauses on hover so a video can be picked */}
      <div className="group/row relative w-full overflow-hidden" data-id="element-3593" data-mp="254hn2xvmgcz2">
        <div className="flex w-max items-start animate-videos-marquee group-hover/row:[animation-play-state:paused] group-focus-within/row:[animation-play-state:paused]" data-id="element-3594" data-mp="255sqgwl5nipn">
          {loop.map((video, i) => <VideoCard key={`${video.youtubeId}-${i}`} video={video} isEs={isEs} onPlay={(v, title) => setPlaying({
          video: v,
          title
        })} data-id="element-3595" data-mp="1qf3cxnk81rim" data-mpi={i} />)}
        </div>
      </div>

      <AnimatePresence data-id="element-3596" data-mp="2j4sfi5ckxzmc">
        {playing ? <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.18,
        ease: EASE
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 sm:p-10" onClick={() => setPlaying(null)} role="dialog" aria-modal="true" aria-label={playing.title || (isEs ? 'Video' : 'Video')} data-id="element-3597" data-mp="2j1whkh0qfaxw">
            <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.96
        }} transition={{
          duration: 0.22,
          ease: EASE
        }} onClick={e => e.stopPropagation()} className="w-full max-w-5xl" data-id="element-3598" data-mp="3lwuyy6zaeld0">
              <div className="relative aspect-video w-full overflow-hidden rounded-xl bg-black shadow-2xl" data-id="element-3599" data-mp="328m4tre6o5av">
                <iframe src={`https://www.youtube-nocookie.com/embed/${playing.video.youtubeId}?autoplay=1&rel=0&playsinline=1${playing.video.start ? `&start=${playing.video.start}` : ''}`} title={playing.title || 'YouTube video'} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className="absolute inset-0 h-full w-full" data-id="element-3600" data-mp="36zm0yufhajcb" />
              </div>
              {playing.title ? <p className="mt-4 font-nunito text-base font-bold text-white" data-id="element-3601" data-mp="20lpl47laeqhx">
                  {playing.title}
                </p> : null}
            </motion.div>
            <button type="button" onClick={() => setPlaying(null)} className="absolute top-4 right-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition-colors duration-150 hover:bg-white/30" aria-label={isEs ? 'Cerrar' : 'Close'} data-id="element-3602" data-mp="29lvtjf24p0a8">
              <XIcon className="h-5 w-5" data-id="element-3603" data-mp="2xa5b24sb71vk" />
            </button>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}